import { firestore } from "firebase/app";
import { DateTime } from "luxon";
import {
  ProjectAssignmentDetails,
  ProjectAssignmentInfo,
  formatProjectAssignmentDetails,
} from "./ProjectAssignmentInfo";
import { SampleInfo } from "./SampleInfo";
import {
  RunEndDateAnomaly,
  RunEndDateCorrection,
} from "./RunEndDateCorrection";
import { ComBoxSensorType } from "./ComBoxSensorData";
import {
  ProgramChain,
  getProjectAssignmentDetailsOfProgramChain,
} from "./ProgramChain";

export interface RunOverview {
  id: string;

  startDate: firestore.Timestamp;
  endDate: firestore.Timestamp;

  wallMaxTemp: number;
  wallMinTemp: number;

  durationInMin: number;
  chainNumber: number;
  chainEnd?: boolean;
  chainId?: string;

  steps: number;
  cycles: number;

  t0: number;

  programNumber: number;
  programRunID: number;
  groundMaxTemperature?: number;
  groundMinTemperature?: number;

  initialMeterReading?: number;
  latestMeterReading?: number;

  /**
   * A rebuilt end date for a run whose Panel lost its clock, written by IRES Cloud beside `endDate`
   * and never over it. Read it through `getEffectiveEndDate`, never directly, so every consumer
   * applies the same rule.
   *
   * @see RunEndDateCorrection
   */
  endDateCorrection?: RunEndDateCorrection;

  /**
   * Set when this run's dates are impossible whether or not a correction could be made. Present
   * WITHOUT `endDateCorrection` means we know the run is wrong and are deliberately not rebuilding
   * it; show the raw value.
   *
   * @see RunEndDateAnomaly
   */
  endDateAnomaly?: RunEndDateAnomaly;

  percentage: number;
  power: number;

  assignedProjectId?: string;
  assignedClientId?: string;
  assignedProjectDetails?: ProjectAssignmentInfo;

  projectAssignmentDetails?: ProjectAssignmentDetails;

  sensorConfig: ComBoxSensorType[];
  sensorUsage?: string[];

  /**
   * Per-slot BLE sensor serial numbers, keyed by slot number (as string).
   * Snapshot from comBoxStatus at run creation time.
   */
  sensorSlotSerials?: Record<string, string>;

  samples: Array<SampleInfo>;
  numberInChain: number;

  sampleFormatVersion?: number;
  activeRun?: boolean;
  hasFilteredMonitorSamples?: boolean;
}

/**
 * The latest stored end among `runs`, in milliseconds; `-Infinity` when there are none.
 *
 * A chain's runs are ordered by start, and that is not always the order in which they ended: a box
 * can stamp a run's start before the run it follows started (BS075 stamped run 81 of drying 35 44 s
 * before run 80 began, then ran it for a week). So the run listed just before another is not
 * necessarily the last one to end before it.
 */
export function getLatestRunEndMillis(runs: RunOverview[]) {
  return Math.max(...runs.map((run) => run.endDate.toMillis()));
}

/**
 * Whether `run` started more than 5 minutes after every one of `previousRuns` had ended: a power
 * cut. Judged on the latest end, not on the run listed just before (see `getLatestRunEndMillis`);
 * judging on that one turned BS075's out-of-order run into a week-long power cut.
 */
export function wasRunInterrupted(run: RunOverview, previousRuns: RunOverview[]) {
  if (previousRuns.length === 0) {
    return false;
  }

  return (
    run.startDate.toMillis() - getLatestRunEndMillis(previousRuns) > 1000 * 60 * 5
  );
}

export function getInitialMeterReading(runOverview: RunOverview) {
  if (
    runOverview.initialMeterReading ||
    runOverview.initialMeterReading === 0
  ) {
    return runOverview.initialMeterReading;
  }

  if (
    runOverview.latestMeterReading !== undefined &&
    runOverview.latestMeterReading !== null
  ) {
    return runOverview.latestMeterReading - runOverview.power;
  }

  return null;
}

export function isActiveRun(runOverview: RunOverview) {
  return (
    runOverview.activeRun || runOverview.endDate.toDate().getFullYear() > 2150
  );
}

export function getAdjustedEndDate(
  runOverview: RunOverview,
  alwaysUserDuration?: boolean
): Date {
  if (alwaysUserDuration || isActiveRun(runOverview)) {
    return DateTime.fromMillis(
      runOverview.startDate.toMillis() + runOverview.durationInMin * 60 * 1000
    ).toJSDate();
  } else {
    return runOverview.endDate.toDate();
  }
}

export function getComBoxRunProgramName(vue: Vue, run: RunOverview) {
  if (!run.programNumber) {
    return vue.$t("comBoxDetail.monitor").toString();
  } else {
    return vue
      .$t("panelDetail.runInfo.programPX", [run.programNumber])
      .toString();
  }
}

export function isTemperatureSet(runOverview: RunOverview) {
  return runOverview.wallMaxTemp < 1000 && runOverview.wallMaxTemp > -1000;
}

export function isIdleRun(runOverview: RunOverview) {
  return runOverview.programNumber === 0 || runOverview.programNumber === 255;
}

export function setProjectAssignmentInfoOfRuns(
  vue: Vue,
  programChains: ProgramChain[],
  sortedRunOverviews: Array<RunOverview>,
  currentProjectAssignmentDetails: ProjectAssignmentDetails | null
) {
  let previousAssignmentDetails: null | ProjectAssignmentDetails = null;

  const programChainLookup = new Map<string, ProgramChain>();

  programChains.forEach((programChain) => {
    programChainLookup.set(programChain.id, programChain);
  });

  const result = [];

  for (let i = 0; i < sortedRunOverviews.length; ++i) {
    const runOverview = sortedRunOverviews[i];
    const matchingProgramChain = programChainLookup.get(
      runOverview.chainId ?? ""
    );

    const assignmentDetails = matchingProgramChain
      ? getProjectAssignmentDetailsOfProgramChain(
          matchingProgramChain,
          currentProjectAssignmentDetails
        )
      : null;

    result.push({
      runOverview: runOverview,
      runAssignmentInfo: formatProjectAssignmentDetails(
        vue,
        assignmentDetails,
        previousAssignmentDetails
      ),
    });

    previousAssignmentDetails = assignmentDetails;
  }

  return result;
}
